import { CLIENT_METRIC_EVENT_LIMITS, type BugReportContext, type ClientMetricEventBatch } from '@sorrem/shared';
import { addBearerToken } from '@sorrem-client/ui/shared/auth-session';
import { ClientMetricReporter, type ClientCrashContext, type ClientCrashInput, type ClientMetricEventRecord } from './client-metric-reporter';
import type { ClientDisplayInfo } from './run-metrics';

const CLIENT_EVENTS_URL = '/api/telemetry/client-events';

/** The release this page was built from: the deploy tag, or `local` for a dev build. */
export const CLIENT_RELEASE_ID: string = import.meta.env.VITE_SORREM_RELEASE_ID?.trim() || 'local';

let reporter: ClientMetricReporter | null = null;
let getContext: () => ClientCrashContext = () => ({});

/**
 * Reports a crash (#212). Safe to call before `installCrashReporting`, and
 * from anywhere: it never throws and never logs, so it can't feed back into
 * the console capture or its own error handlers.
 */
export function reportClientCrash(input: ClientCrashInput): void {
    try {
        ensureReporter().reportCrash(input);
    } catch {
        // Reporting must never become the crash.
    }
}

/** Records a client metric event (#213). Never throws. */
export function recordClientMetricEvent(event: ClientMetricEventRecord): void {
    try {
        ensureReporter().record(event);
    } catch {
        // Telemetry must never break the page.
    }
}

/**
 * What a bug report attaches (#218): where the player is, the run and its
 * frame timing, the recent console lines, the window and GPU, and the
 * release. Shown to the tester before it is sent.
 */
export function resolveBugReportContext(): BugReportContext {
    let runContext: ClientCrashContext = {};
    try {
        runContext = getContext();
    } catch {
        // Without a run, the report just has no run.
    }
    return {
        route: window.location.pathname.slice(0, CLIENT_METRIC_EVENT_LIMITS.maxRouteLength),
        ...(runContext.crucibleId !== undefined ? { crucibleId: runContext.crucibleId } : {}),
        consoleTail: ensureReporter().getConsoleTail(),
        ...(runContext.frameTiming !== undefined ? { frameTiming: runContext.frameTiming } : {}),
        ...getClientDisplayInfo(),
        clientVersion: CLIENT_RELEASE_ID.slice(0, CLIENT_METRIC_EVENT_LIMITS.maxIdLength),
    };
}

/**
 * Starts capturing crashes for this page: uncaught errors, unhandled
 * rejections and the recent console errors and warnings. `context` reads the
 * run's id and frame timing at the moment of each report. Call once, before
 * the app renders.
 */
export function installCrashReporting(context: () => ClientCrashContext): void {
    getContext = context;
    const active = ensureReporter();

    window.addEventListener('error', (event: ErrorEvent) => {
        const error: unknown = event.error;
        reportClientCrash({
            kind: 'error',
            message: error instanceof Error ? error.message : event.message,
            stack: error instanceof Error && error.stack ? error.stack : formatLocation(event),
        });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason: unknown = event.reason;
        reportClientCrash({
            kind: 'unhandled-rejection',
            message: reason instanceof Error ? reason.message : formatValue(reason),
            stack: reason instanceof Error ? reason.stack : undefined,
        });
    });

    // keepalive lets the last batch outlive the page.
    window.addEventListener('pagehide', () => active.flush());

    captureConsole(active, 'error');
    captureConsole(active, 'warn');
}

function ensureReporter(): ClientMetricReporter {
    reporter ??= new ClientMetricReporter({
        send: sendBatch,
        clientVersion: CLIENT_RELEASE_ID,
        getRoute: () => window.location.pathname,
        getContext: () => {
            try {
                return getContext();
            } catch {
                return {};
            }
        },
    });
    return reporter;
}

function sendBatch(batch: ClientMetricEventBatch): void {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    addBearerToken(headers);

    try {
        fetch(CLIENT_EVENTS_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify(batch),
            keepalive: true,
        }).catch(() => {
            // Offline, or the server is down: the report is lost, by design.
        });
    } catch {
        // No fetch (a test environment) or a refused request: lost, by design.
    }
}

/** Wraps `console[level]` so each line is also kept for the next report. */
function captureConsole(active: ClientMetricReporter, level: 'error' | 'warn'): void {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]): void => {
        try {
            active.recordConsoleLine(`[${level}] ${args.map(formatValue).join(' ')}`);
        } catch {
            // Never let the capture break logging.
        }
        original(...args);
    };
}

function formatLocation(event: ErrorEvent): string | undefined {
    return event.filename ? `at ${event.filename}:${event.lineno}:${event.colno}` : undefined;
}

function formatValue(value: unknown): string {
    if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
    }
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        return String(value);
    }
}

/** The GPU's name once looked up; `name` is missing when the browser won't say. */
let gpuRenderer: { readonly name?: string } | undefined;

/** The window and GPU, for a performance sample. The GPU is looked up once. */
export function getClientDisplayInfo(): ClientDisplayInfo {
    gpuRenderer ??= { name: findGpuRenderer() };
    return {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        ...(gpuRenderer.name !== undefined ? { gpuRenderer: gpuRenderer.name.slice(0, CLIENT_METRIC_EVENT_LIMITS.maxShortTextLength) } : {}),
    };
}

/** The GPU as the browser names it, from a throwaway WebGL context; undefined if it won't say. */
function findGpuRenderer(): string | undefined {
    try {
        const gl = document.createElement('canvas').getContext('webgl');
        if (!gl) {
            return undefined;
        }
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        const renderer: unknown = gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return typeof renderer === 'string' && renderer.length > 0 ? renderer : undefined;
    } catch {
        return undefined;
    }
}
